import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExportDateRangeValue, ExportField, ExportFieldValue } from '../types';
import { datePresetRange, fieldProblem, isDateRange, presetFits } from '../utils/exportFlow';

interface ExportFlowFieldProps {
    field: ExportField;
    value: ExportFieldValue | undefined;
    onChange: (value: ExportFieldValue) => void;
    /** Unieke prefix voor de id's, want dezelfde key kan in meer stappen voorkomen */
    idPrefix: string;
    /** Validatiefout van de server bij dit veld; als platte tekst tonen */
    serverError?: string | null;
    disabled?: boolean;
    autoFocus?: boolean;
}

export const exportInputClass = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50';

/** Eén veld uit een stap van de exportwizard; labels en teksten komen van de server. */
const ExportFlowField: React.FC<ExportFlowFieldProps> = ({
    field, value, onChange, idPrefix, serverError, disabled, autoFocus,
}) => {
    const { t } = useTranslation();
    const id = `${idPrefix}-${field.key}`;
    const problem = fieldProblem(field, value);
    // Lege verplichte velden houden de knop tegen; een melding is pas nodig
    // bij invoer die echt niet klopt
    const localError = problem && (problem.kind === 'maxLength' || problem.kind === 'dateOrder' || problem.kind === 'maxDays')
        ? t(`exportWizard.fieldErrors.${problem.kind}`, 'count' in problem ? { count: problem.count } : undefined)
        : null;
    const error = serverError || localError;
    const errorId = error ? `${id}-error` : undefined;

    const errorLine = error ? (
        <p id={errorId} role="alert" className="mt-1 text-xs text-red-600 whitespace-pre-line">{error}</p>
    ) : null;

    if (field.type === 'toggle') {
        return (
            <div>
                <label htmlFor={id} className="inline-flex items-center gap-2 text-sm text-gray-900">
                    <input
                        id={id}
                        type="checkbox"
                        checked={value === true}
                        onChange={(event) => onChange(event.target.checked)}
                        disabled={disabled}
                        aria-describedby={errorId}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    {field.label}
                </label>
                {errorLine}
            </div>
        );
    }

    if (field.type === 'date_range') {
        const range: ExportDateRangeValue = isDateRange(value) ? value : field.default;
        const presets = (field.presets ?? [])
            .filter((preset) => preset === 'today' || preset === 'week' || preset === 'month')
            .map((preset) => ({ preset, range: datePresetRange(preset) }))
            .filter((item) => presetFits(item.range, field.max_days));
        return (
            <fieldset aria-describedby={errorId}>
                <legend className="block text-sm font-medium text-gray-700 mb-2">{field.label}</legend>
                <div className="flex flex-wrap items-end gap-2">
                    <label className="flex-1 min-w-[8rem] text-xs text-gray-500">
                        {t('exportWizard.dateRange.start')}
                        <input
                            id={`${id}-start`}
                            type="date"
                            value={range.start}
                            onChange={(event) => onChange({ ...range, start: event.target.value })}
                            disabled={disabled}
                            className={`${exportInputClass} mt-1`}
                        />
                    </label>
                    <label className="flex-1 min-w-[8rem] text-xs text-gray-500">
                        {t('exportWizard.dateRange.end')}
                        <input
                            id={`${id}-end`}
                            type="date"
                            value={range.end}
                            min={range.start || undefined}
                            onChange={(event) => onChange({ ...range, end: event.target.value })}
                            disabled={disabled}
                            className={`${exportInputClass} mt-1`}
                        />
                    </label>
                </div>
                {presets.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {presets.map(({ preset, range: presetRange }) => {
                            const active = presetRange.start === range.start && presetRange.end === range.end;
                            return (
                                <button
                                    key={preset}
                                    type="button"
                                    onClick={() => onChange(presetRange)}
                                    disabled={disabled}
                                    aria-pressed={active}
                                    className={`px-3 py-1 text-xs rounded-lg border transition-colors disabled:opacity-50 ${active
                                        ? 'bg-primary-50 border-primary-300 text-primary-700'
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {t(`exportWizard.dateRange.presets.${preset}`)}
                                </button>
                            );
                        })}
                    </div>
                )}
                {errorLine}
            </fieldset>
        );
    }

    if (field.type === 'select') {
        const selected = typeof value === 'string' ? value : '';
        const hasValidValue = field.options.some((option) => option.value === selected);
        return (
            <div>
                <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                <select
                    id={id}
                    value={hasValidValue ? selected : ''}
                    onChange={(event) => onChange(event.target.value)}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    aria-describedby={errorId}
                    aria-invalid={Boolean(error) || undefined}
                    className={exportInputClass}
                >
                    {(!hasValidValue || field.required === false) && (
                        <option value="">{t('exportWizard.select.placeholder')}</option>
                    )}
                    {field.options.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
                {errorLine}
            </div>
        );
    }

    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
            <input
                id={id}
                type="text"
                value={typeof value === 'string' ? value : ''}
                onChange={(event) => onChange(event.target.value)}
                placeholder={field.placeholder}
                maxLength={field.max_length}
                disabled={disabled}
                autoFocus={autoFocus}
                autoComplete="off"
                aria-describedby={errorId}
                aria-invalid={Boolean(error) || undefined}
                className={exportInputClass}
            />
            {errorLine}
        </div>
    );
};

export default ExportFlowField;
